import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    makeStyles,
    Paper,
    Step,
    StepLabel,
    Stepper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import Autocomplete from "@material-ui/lab/Autocomplete";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import GroupIcon from "@material-ui/icons/Group";
import {
    useApi,
    fetchApiRef,
    discoveryApiRef,
} from "@backstage/core-plugin-api";
import { useEffect, useMemo, useState } from "react";

interface TeamMember {
    name?: string | null;
    email?: string | null;
    tribe?: string | null;
    team?: string | null;
    domain?: string | null;
    location?: string | null;
    skills?: string | null;
    certifications?: string | null;
    vendorname?: string | null;
}

interface TeamMembersManagementDialogProps {
    open: boolean;
    onClose: () => void;
    members: TeamMember[];
    onRefresh: () => void;
}

const emptyMemberForm: TeamMember = {
    email: "",
    name: "",
    tribe: "",
    team: "",
    domain: "cloud",
    location: "",
    skills: "",
    certifications: "",
    vendorname: "",
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const isValidEmail = (email?: string | null) => {
    const trimmedEmail = email?.trim();

    if (!trimmedEmail) {
        return false;
    }

    return EMAIL_PATTERN.test(trimmedEmail);
};

const REQUIRED_FIELD_LABELS: ReadonlyArray<[keyof TeamMember, string]> = [
    ["email", "Email"],
    ["name", "Name"],
    ["tribe", "Tribe"],
    ["team", "Team"],
    ["domain", "Domain"],
    ["location", "Location"],
];

const ADD_MEMBER_STEPS = [
    "User details",
    "Team details",
    "Skills & certifications",
];

const STEP_REQUIRED_FIELDS: ReadonlyArray<
    ReadonlyArray<[keyof TeamMember, string]>
> = [
        [
            ["email", "Email"],
            ["name", "Name"],
        ],
        [
            ["tribe", "Tribe"],
            ["team", "Team"],
            ["domain", "Domain"],
            ["location", "Location"],
        ],
        [],
    ];

const getMissingRequiredFields = (member: TeamMember) => {
    return REQUIRED_FIELD_LABELS.filter(([field]) => !member[field]?.trim()).map(
        ([, label]) => label,
    );
};

const getMissingStepRequiredFields = (member: TeamMember, step: number) => {
    return (STEP_REQUIRED_FIELDS[step] || [])
        .filter(([field]) => !member[field]?.trim())
        .map(([, label]) => label);
};

const getStepValidationMessages = (member: TeamMember, step: number) => {
    const messages = getMissingStepRequiredFields(member, step).map(
        (label) => `${label} is required`,
    );

    if (step === 0 && member.email?.trim() && !isValidEmail(member.email)) {
        messages.push("Enter a valid email address");
    }

    return messages;
};

const getFormValidationMessages = (member: TeamMember) => {
    const messages = getMissingRequiredFields(member).map(
        (label) => `${label} is required`,
    );

    if (member.email?.trim() && !isValidEmail(member.email)) {
        messages.push("Enter a valid email address");
    }

    return messages;
};

const getUniqueMemberOptions = (
    members: TeamMember[],
    field: keyof TeamMember,
) => {
    return Array.from(
        new Set(
            members
                .map((member) => member[field]?.trim())
                .filter((value): value is string => Boolean(value)),
        ),
    ).sort((a, b) => a.localeCompare(b));
};

const getOptionsWithCurrentValue = (
    options: string[],
    currentValue?: string | null,
) => {
    const trimmedCurrentValue = currentValue?.trim();

    if (!trimmedCurrentValue || options.includes(trimmedCurrentValue)) {
        return options;
    }

    return [...options, trimmedCurrentValue].sort((a, b) => a.localeCompare(b));
};

type SortDirection = "asc" | "desc" | "normal";
type SortableMemberField = Extract<
    keyof TeamMember,
    "email" | "name" | "tribe" | "team" | "location" | "certifications"
>;

const SORTABLE_COLUMNS: ReadonlyArray<{
    field: SortableMemberField;
    label: string;
}> = [
        { field: "email", label: "Email" },
        { field: "name", label: "Name" },
        { field: "tribe", label: "Tribe" },
        { field: "team", label: "Team" },
        { field: "location", label: "Location" },
        { field: "certifications", label: "Certifications" },
    ];

const getNextSortDirection = (currentDirection: SortDirection) => {
    if (currentDirection === "normal") {
        return "asc";
    }

    if (currentDirection === "asc") {
        return "desc";
    }

    return "normal";
};

const compareMemberValues = (
    a: TeamMember,
    b: TeamMember,
    field: SortableMemberField,
) => {
    const valueA = a[field]?.trim() || "";
    const valueB = b[field]?.trim() || "";

    return valueA.localeCompare(valueB, undefined, {
        numeric: true,
        sensitivity: "base",
    });
};

const SEARCHABLE_MEMBER_FIELDS: ReadonlyArray<keyof TeamMember> = [
    "email",
    "name",
    "tribe",
    "team",
    "domain",
    "location",
    "skills",
    "certifications",
    "vendorname",
];

const memberMatchesSearch = (member: TeamMember, searchQuery: string) => {
    const normalizedSearchQuery = searchQuery.trim().toLowerCase();

    if (!normalizedSearchQuery) {
        return true;
    }

    return SEARCHABLE_MEMBER_FIELDS.some((field) =>
        member[field]?.toLowerCase().includes(normalizedSearchQuery),
    );
};

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        borderRadius: 18,
        overflow: "hidden",
    },
    formDialogPaper: {
        maxWidth: 780,
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.22)",
    },
    dialogTitle: {
        padding: 0,
        background: "linear-gradient(135deg, #f8fafc 0%, #eef6ff 100%)",
        borderBottom: "1px solid #dbe7f3",
    },
    header: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        padding: theme.spacing(2.5, 3),
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    headerIcon: {
        width: 42,
        height: 42,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 12,
        color: "#075985",
        backgroundColor: "#e0f2fe",
        border: "1px solid #bae6fd",
    },
    titleText: {
        fontWeight: 800,
        color: "#0f172a",
        lineHeight: 1.2,
    },
    subtitleText: {
        color: "#64748b",
        marginTop: 2,
    },
    memberCountChip: {
        fontWeight: 700,
        color: "#334155",
        backgroundColor: "#ffffff",
        borderColor: "#cbd5e1",
    },
    dialogContent: {
        minWidth: 920,
        padding: theme.spacing(3),
        backgroundColor: "#f8fafc",
    },
    formContent: {
        padding: theme.spacing(3),
        backgroundColor: "#f8fafc",
    },
    formHeaderIcon: {
        width: 44,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 14,
        color: "#1d4ed8",
        backgroundColor: "#dbeafe",
        border: "1px solid #bfdbfe",
    },
    stepper: {
        padding: theme.spacing(2.5, 1, 2),
        marginBottom: theme.spacing(2),
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
        boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
        "& .MuiStepIcon-root": {
            color: "#cbd5e1",
        },
        "& .MuiStepIcon-root.MuiStepIcon-active": {
            color: "#2563eb",
        },
        "& .MuiStepIcon-root.MuiStepIcon-completed": {
            color: "#16a34a",
        },
        "& .MuiStepLabel-label": {
            marginTop: theme.spacing(1),
            fontWeight: 700,
            color: "#64748b",
        },
        "& .MuiStepLabel-label.MuiStepLabel-active": {
            color: "#0f172a",
        },
        "& .MuiStepConnector-line": {
            borderColor: "#cbd5e1",
        },
    },
    stepSection: {
        minHeight: 0,
    },
    stepCard: {
        padding: theme.spacing(2.5),
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 18,
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
    },
    stepCardHeader: {
        marginBottom: theme.spacing(2),
    },
    stepEyebrow: {
        color: "#2563eb",
        fontSize: 12,
        fontWeight: 800,
        letterSpacing: 0.4,
        textTransform: "uppercase",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    toolbarActions: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1.5),
    },
    searchField: {
        width: 300,
        backgroundColor: "#ffffff",
        borderRadius: 10,
    },
    sectionTitle: {
        fontWeight: 800,
        color: "#0f172a",
    },
    sectionDescription: {
        color: "#64748b",
        marginTop: 2,
    },
    primaryButton: {
        borderRadius: 10,
        boxShadow: "0 8px 18px rgba(37, 99, 235, 0.18)",
        textTransform: "none",
        fontWeight: 700,
    },
    secondaryButton: {
        borderRadius: 10,
        textTransform: "none",
        fontWeight: 700,
    },
    tableContainer: {
        maxHeight: 390,
        borderRadius: 16,
        border: "1px solid #dbe7f3",
        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.06)",
        overflow: "auto",
        // Force a light, neutral scrollbar regardless of OS / Backstage theme.
        // The default dark-theme scrollbar looks jarring against this light table.
        scrollbarWidth: "thin",
        scrollbarColor: "#cbd5e1 #f1f5f9",
        "&::-webkit-scrollbar": {
            width: 10,
            height: 10,
            backgroundColor: "#f1f5f9",
        },
        "&::-webkit-scrollbar-track": {
            backgroundColor: "#f1f5f9",
            borderRadius: 8,
        },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#cbd5e1",
            borderRadius: 8,
            border: "2px solid #f1f5f9",
        },
        "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "#94a3b8",
        },
        "&::-webkit-scrollbar-corner": {
            backgroundColor: "#f1f5f9",
        },
    },
    table: {
        backgroundColor: "#ffffff",
    },
    tableHeadCell: {
        top: 0,
        zIndex: 2,
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: 0.35,
        textTransform: "uppercase",
        backgroundColor: "#f1f5f9",
        borderBottom: "1px solid #cbd5e1",
        color: "#334155",
        padding: "14px 16px",
        whiteSpace: "nowrap",
    },
    sortLabel: {
        color: "inherit",
        "&&:hover": {
            color: "#2563eb",
        },
        "&&.MuiTableSortLabel-active": {
            color: "#2563eb",
        },
        "&& .MuiTableSortLabel-icon": {
            color: "#2563eb !important",
        },
    },
    tableCell: {
        color: "#334155",
        fontSize: 13,
        borderBottom: "1px solid #eef2f7",
        padding: "13px 16px",
        whiteSpace: "nowrap",
    },
    emailCell: {
        color: "#0f172a",
        fontWeight: 700,
    },
    tableRow: {
        transition: "background-color 120ms ease, box-shadow 120ms ease",
        "&:hover": {
            backgroundColor: "#f0f7ff",
        },
    },
    actionCell: {
        width: 104,
        padding: theme.spacing(0.75, 1.25),
        whiteSpace: "nowrap",
    },
    iconButton: {
        padding: theme.spacing(0.75),
        marginLeft: theme.spacing(0.5),
        border: "1px solid transparent",
        "&:hover": {
            backgroundColor: "#ffffff",
            borderColor: "#cbd5e1",
        },
    },
    deleteButton: {
        color: "#b91c1c",
    },
    alertBox: {
        marginBottom: theme.spacing(2),
        borderRadius: 12,
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: theme.spacing(1),
        minHeight: 220,
        padding: theme.spacing(4),
        textAlign: "center",
        color: "#64748b",
        backgroundColor: "#ffffff",
        border: "1px dashed #cbd5e1",
        borderRadius: 16,
    },
    formGrid: {
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: theme.spacing(2),
        marginTop: theme.spacing(1.5),
    },
    fullWidthField: {
        gridColumn: "1 / -1",
    },
    dialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
    },
    formDialogActions: {
        padding: theme.spacing(2, 3),
        borderTop: "1px solid #e2e8f0",
        backgroundColor: "#ffffff",
        justifyContent: "space-between",
    },
    actionGroup: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
    },
    certificationText: {
        display: "-webkit-box",
        maxWidth: 220,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "normal",
        WebkitBoxOrient: "vertical",
        WebkitLineClamp: 2,
        lineHeight: 1.35,
    },
    deleteDialogPaper: {
        maxWidth: 520,
        borderRadius: 20,
        overflow: "hidden",
        boxShadow: "0 24px 70px rgba(15, 23, 42, 0.24)",
    },
    deleteHeaderIcon: {
        width: 44,
        height: 44,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        borderRadius: 14,
        color: "#b91c1c",
        backgroundColor: "#fee2e2",
        border: "1px solid #fecaca",
    },
    deleteContent: {
        padding: theme.spacing(3),
        backgroundColor: "#f8fafc",
    },
    deleteSummaryCard: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#ffffff",
        border: "1px solid #e2e8f0",
        borderRadius: 16,
    },
    deleteSummaryRow: {
        display: "grid",
        gridTemplateColumns: "110px 1fr",
        gap: theme.spacing(1.5),
        padding: theme.spacing(1, 0),
        borderBottom: "1px solid #eef2f7",
        "&:last-child": {
            borderBottom: 0,
        },
    },
    deleteSummaryLabel: {
        color: "#64748b",
        fontSize: 13,
        fontWeight: 700,
    },
    deleteSummaryValue: {
        color: "#0f172a",
        fontSize: 13,
        fontWeight: 700,
        wordBreak: "break-word",
    },
    dangerButton: {
        borderRadius: 10,
        color: "#ffffff",
        backgroundColor: "#dc2626",
        textTransform: "none",
        fontWeight: 700,
        boxShadow: "0 8px 18px rgba(220, 38, 38, 0.18)",
        "&:hover": {
            backgroundColor: "#b91c1c",
        },
    },
    mutedText: {
        color: "#94a3b8",
    },
}));

export const TeamMembersManagementDialog = (
    props: TeamMembersManagementDialogProps,
) => {
    const { open, onClose, members, onRefresh } = props;
    const classes = useStyles();
    const fetchApi = useApi(fetchApiRef);
    const discoveryApi = useApi(discoveryApiRef);

    const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [activeStep, setActiveStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | undefined>();
    const [success, setSuccess] = useState<string | undefined>();
    const [formData, setFormData] = useState<TeamMember>(emptyMemberForm);
    const [memberToDelete, setMemberToDelete] = useState<TeamMember | null>(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [sortField, setSortField] = useState<SortableMemberField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>("normal");
    const [localMembers, setLocalMembers] = useState<TeamMember[]>(members);

    useEffect(() => {
        setLocalMembers(members);
    }, [members]);

    const tribeOptions = useMemo(() => {
        return getUniqueMemberOptions(localMembers, "tribe");
    }, [localMembers]);

    const teamOptions = useMemo(() => {
        return getUniqueMemberOptions(localMembers, "team");
    }, [localMembers]);

    const locationOptions = useMemo(() => {
        return getUniqueMemberOptions(localMembers, "location");
    }, [localMembers]);

    const visibleTribeOptions = useMemo(() => {
        return getOptionsWithCurrentValue(tribeOptions, formData.tribe);
    }, [tribeOptions, formData.tribe]);

    const visibleTeamOptions = useMemo(() => {
        return getOptionsWithCurrentValue(teamOptions, formData.team);
    }, [teamOptions, formData.team]);

    const visibleLocationOptions = useMemo(() => {
        return getOptionsWithCurrentValue(locationOptions, formData.location);
    }, [locationOptions, formData.location]);

    const displayedMembers = useMemo(() => {
        const filteredMembers = localMembers.filter((member) =>
            memberMatchesSearch(member, searchQuery),
        );

        if (!sortField || sortDirection === "normal") {
            return filteredMembers;
        }

        return [...filteredMembers].sort((a, b) => {
            const comparison = compareMemberValues(a, b, sortField);
            return sortDirection === "asc" ? comparison : -comparison;
        });
    }, [localMembers, searchQuery, sortDirection, sortField]);

    const resetForm = () => {
        setFormData(emptyMemberForm);
        setEditingMember(null);
        setError(undefined);
        setActiveStep(0);
    };

    const handleOpenForm = (member?: TeamMember) => {
        setSuccess(undefined);

        if (member) {
            setEditingMember(member);
            setFormData({ ...emptyMemberForm, ...member });
        } else {
            resetForm();
        }

        setActiveStep(0);
        setIsFormOpen(true);
    };

    const handleCloseForm = () => {
        setIsFormOpen(false);
        resetForm();
    };

    const handleFormChange =
        (field: keyof TeamMember) =>
            (event: React.ChangeEvent<HTMLInputElement>) => {
                setFormData((prev) => ({
                    ...prev,
                    [field]: event.target.value,
                }));
            };

    const handleAutocompleteChange =
        (field: keyof TeamMember) =>
            (_event: React.ChangeEvent<{}>, value: string | null) => {
                setFormData((prev) => ({
                    ...prev,
                    [field]: value || "",
                }));
            };

    const handleAutocompleteInputChange =
        (field: keyof TeamMember) =>
            (_event: React.ChangeEvent<{}>, value: string) => {
                setFormData((prev) => ({
                    ...prev,
                    [field]: value,
                }));
            };

    const handleSort = (field: SortableMemberField) => {
        if (sortField !== field) {
            setSortField(field);
            setSortDirection("asc");
            return;
        }

        const nextDirection = getNextSortDirection(sortDirection);
        setSortDirection(nextDirection);

        if (nextDirection === "normal") {
            setSortField(null);
        }
    };

    const handleNextStep = () => {
        const validationMessages = getStepValidationMessages(
            { ...formData, domain: "cloud" },
            activeStep,
        );

        if (validationMessages.length > 0) {
            setError(validationMessages.join(". "));
            return;
        }

        setError(undefined);
        setActiveStep((prevStep) =>
            Math.min(prevStep + 1, ADD_MEMBER_STEPS.length - 1),
        );
    };

    const handleBackStep = () => {
        setError(undefined);
        setActiveStep((prevStep) => Math.max(prevStep - 1, 0));
    };

    const handleSubmit = async () => {
        try {
            setLoading(true);
            setError(undefined);
            setSuccess(undefined);

            const normalizedFormData = {
                ...formData,
                domain: "cloud",
            };
            const validationMessages = getFormValidationMessages(normalizedFormData);

            if (validationMessages.length > 0) {
                setError(validationMessages.join(". "));
                return;
            }

            const baseUrl = await discoveryApi.getBaseUrl("metrics");
            const payload = {
                email: normalizedFormData.email!.toLowerCase().trim(),
                name: normalizedFormData.name!.trim(),
                tribe: normalizedFormData.tribe!.trim(),
                team: normalizedFormData.team!.trim(),
                domain: "cloud",
                location: normalizedFormData.location!.trim(),
                skills: formData.skills || null,
                certifications: formData.certifications || null,
                vendorname: formData.vendorname || null,
            };

            if (editingMember) {
                if (!editingMember.email) {
                    throw new Error("Cannot update member without an email");
                }

                const response = await fetchApi.fetch(
                    `${baseUrl}/teams-members/${editingMember.email}`,
                    {
                        method: "PUT",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify(payload),
                    },
                );

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        errorData.error ||
                        `Failed to update team member: ${response.status}`,
                    );
                }

                const updatedMember: TeamMember = {
                    ...payload,
                    skills: payload.skills || "",
                    certifications: payload.certifications || "",
                    vendorname: payload.vendorname || "",
                };

                setLocalMembers((prevMembers) =>
                    prevMembers.map((member) =>
                        member.email?.trim().toLowerCase() ===
                            editingMember.email?.trim().toLowerCase()
                            ? updatedMember
                            : member,
                    ),
                );
                setSuccess(`Updated ${payload.name} successfully.`);
            } else {
                const response = await fetchApi.fetch(`${baseUrl}/teams-members`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(payload),
                });

                if (!response.ok) {
                    const errorData = await response.json().catch(() => ({}));
                    throw new Error(
                        errorData.error ||
                        `Failed to create team member: ${response.status}`,
                    );
                }

                const newMember: TeamMember = {
                    ...payload,
                    skills: payload.skills || "",
                    certifications: payload.certifications || "",
                    vendorname: payload.vendorname || "",
                };

                setLocalMembers((prevMembers) => [newMember, ...prevMembers]);
                setSuccess(`Added ${payload.name} successfully.`);
            }

            handleCloseForm();
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const handleRequestDelete = (member: TeamMember) => {
        if (!member.email) {
            setError("Cannot delete member without an email");
            return;
        }

        setError(undefined);
        setSuccess(undefined);
        setMemberToDelete(member);
    };

    const handleCloseDeleteDialog = () => {
        if (!loading) {
            setMemberToDelete(null);
        }
    };

    const handleConfirmDelete = async () => {
        const email = memberToDelete?.email?.trim();

        if (!email) {
            setError("Cannot delete member without an email");
            setMemberToDelete(null);
            return;
        }

        try {
            setLoading(true);
            setError(undefined);
            setSuccess(undefined);

            const baseUrl = await discoveryApi.getBaseUrl("metrics");
            const response = await fetchApi.fetch(
                `${baseUrl}/teams-members/${email}`,
                {
                    method: "DELETE",
                },
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(
                    errorData.error || `Failed to delete team member: ${response.status}`,
                );
            }

            setLocalMembers((prevMembers) =>
                prevMembers.filter(
                    (member) => member.email?.trim().toLowerCase() !== email.toLowerCase(),
                ),
            );
            setSuccess(`Deleted ${memberToDelete?.name?.trim() || email} successfully.`);
            setMemberToDelete(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : "An error occurred");
        } finally {
            setLoading(false);
        }
    };

    const handleCloseDialog = () => {
        setError(undefined);
        setSuccess(undefined);
        onRefresh();
        onClose();
    };

    const renderValue = (value?: string | null) => {
        return value ? value : <span className={classes.mutedText}>Not set</span>;
    };

    const renderCertificationValue = (value?: string | null) => {
        const trimmedValue = value?.trim();

        if (!trimmedValue) {
            return <span className={classes.mutedText}>Not set</span>;
        }

        return (
            <Tooltip title={trimmedValue} arrow placement="top">
                <span className={classes.certificationText}>{trimmedValue}</span>
            </Tooltip>
        );
    };

    const emailValue = formData.email?.trim() || "";
    const isEmailInvalid = Boolean(emailValue) && !isValidEmail(emailValue);
    const emailHelperText = isEmailInvalid
        ? "Enter a valid email address, e.g. name@company.com"
        : "Use a valid work email address";
    const isLastStep = activeStep === ADD_MEMBER_STEPS.length - 1;
    const isNextDisabled =
        loading ||
        getStepValidationMessages({ ...formData, domain: "cloud" }, activeStep)
            .length > 0;
    const isSubmitDisabled =
        loading ||
        getFormValidationMessages({ ...formData, domain: "cloud" }).length > 0;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleCloseDialog}
                maxWidth="lg"
                fullWidth
                aria-labelledby="team-members-dialog-title"
                classes={{ paper: classes.dialogPaper }}
            >
                <DialogTitle
                    disableTypography
                    id="team-members-dialog-title"
                    className={classes.dialogTitle}
                >
                    <Box className={classes.header}>
                        <Box className={classes.headerLeft}>
                            <Box className={classes.headerIcon}>
                                <GroupIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" className={classes.titleText}>
                                    Manage Team Members
                                </Typography>
                                <Typography variant="body2" className={classes.subtitleText}>
                                    Maintain ownership, team details, locations, and capability
                                    data.
                                </Typography>
                            </Box>
                        </Box>
                        <Chip
                            label={
                                searchQuery.trim()
                                    ? `${displayedMembers.length} of ${localMembers.length} ${localMembers.length === 1 ? "member" : "members"}`
                                    : `${localMembers.length} ${localMembers.length === 1 ? "member" : "members"}`
                            }
                            variant="outlined"
                            className={classes.memberCountChip}
                        />
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.dialogContent}>
                    {success && (
                        <Alert
                            severity="success"
                            className={classes.alertBox}
                            onClose={() => setSuccess(undefined)}
                        >
                            {success}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" className={classes.alertBox}>
                            {error}
                        </Alert>
                    )}

                    <Box className={classes.toolbar}>
                        <Box>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                Directory
                            </Typography>
                            <Typography
                                variant="body2"
                                className={classes.sectionDescription}
                            >
                                Review existing members or add a new profile.
                            </Typography>
                        </Box>
                        <Box className={classes.toolbarActions}>
                            <TextField
                                label="Search users"
                                placeholder="Name, email, team, location..."
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                                size="small"
                                variant="outlined"
                                className={classes.searchField}
                                inputProps={{ "aria-label": "Search users" }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpenForm()}
                                disabled={loading || isFormOpen}
                                className={classes.primaryButton}
                            >
                                Add team member
                            </Button>
                        </Box>
                    </Box>

                    {localMembers.length === 0 ? (
                        <Box className={classes.emptyState}>
                            <GroupIcon fontSize="large" />
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                No team members yet
                            </Typography>
                            <Typography variant="body2">
                                Add your first member to start building the team directory.
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            component={Paper}
                            className={classes.tableContainer}
                        >
                            <Table size="small" stickyHeader className={classes.table}>
                                <TableHead>
                                    <TableRow>
                                        {SORTABLE_COLUMNS.map((column) => {
                                            const isActive =
                                                sortField === column.field &&
                                                sortDirection !== "normal";

                                            return (
                                                <TableCell
                                                    key={column.field}
                                                    className={classes.tableHeadCell}
                                                    sortDirection={isActive ? sortDirection : false}
                                                >
                                                    <TableSortLabel
                                                        active={isActive}
                                                        direction={
                                                            isActive && sortDirection === "desc"
                                                                ? "desc"
                                                                : "asc"
                                                        }
                                                        onClick={() => handleSort(column.field)}
                                                        className={classes.sortLabel}
                                                        hideSortIcon={false}
                                                    >
                                                        {column.label}
                                                    </TableSortLabel>
                                                </TableCell>
                                            );
                                        })}
                                        <TableCell className={classes.tableHeadCell} align="right">
                                            Actions
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {displayedMembers.length === 0 ? (
                                        <TableRow>
                                            <TableCell className={classes.tableCell} colSpan={7}>
                                                No users match your search.
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        displayedMembers.map((member, index) => (
                                            <TableRow
                                                key={member.email ?? `member-${index}`}
                                                className={classes.tableRow}
                                            >
                                                <TableCell
                                                    className={`${classes.tableCell} ${classes.emailCell}`}
                                                >
                                                    {renderValue(member.email)}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {renderValue(member.name)}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {renderValue(member.tribe)}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {renderValue(member.team)}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {renderValue(member.location)}
                                                </TableCell>
                                                <TableCell className={classes.tableCell}>
                                                    {renderCertificationValue(member.certifications)}
                                                </TableCell>
                                                <TableCell className={classes.actionCell} align="right">
                                                    <IconButton
                                                        size="small"
                                                        className={classes.iconButton}
                                                        onClick={() => handleOpenForm(member)}
                                                        disabled={loading || isFormOpen}
                                                        title="Edit member"
                                                        aria-label="Edit member"
                                                    >
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        className={`${classes.iconButton} ${classes.deleteButton}`}
                                                        onClick={() => handleRequestDelete(member)}
                                                        disabled={loading || isFormOpen}
                                                        title="Delete member"
                                                        aria-label="Delete member"
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseDialog}
                        disabled={loading}
                        className={classes.secondaryButton}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={isFormOpen}
                onClose={handleCloseForm}
                maxWidth="md"
                fullWidth
                aria-labelledby="form-dialog-title"
                classes={{ paper: classes.formDialogPaper }}
            >
                <DialogTitle
                    disableTypography
                    id="form-dialog-title"
                    className={classes.dialogTitle}
                >
                    <Box className={classes.header}>
                        <Box className={classes.headerLeft}>
                            <Box className={classes.formHeaderIcon}>
                                <AddIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" className={classes.titleText}>
                                    {editingMember ? "Edit Team Member" : "Add Team Member"}
                                </Typography>
                                <Typography variant="body2" className={classes.subtitleText}>
                                    {editingMember
                                        ? "Update this member profile in a guided, validated flow."
                                        : "Create a complete profile with validated user and team details."}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.formContent}>
                    {error && (
                        <Alert severity="error" className={classes.alertBox}>
                            {error}
                        </Alert>
                    )}

                    <Stepper
                        activeStep={activeStep}
                        alternativeLabel
                        className={classes.stepper}
                    >
                        {ADD_MEMBER_STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    <Box className={classes.stepCard}>
                        <Box className={classes.stepCardHeader}>
                            <Typography className={classes.stepEyebrow}>
                                Step {activeStep + 1} of {ADD_MEMBER_STEPS.length}
                            </Typography>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                                {ADD_MEMBER_STEPS[activeStep]}
                            </Typography>
                            <Typography
                                variant="body2"
                                className={classes.sectionDescription}
                            >
                                {activeStep === 0 &&
                                    "Start with a valid email address and display name."}
                                {activeStep === 1 &&
                                    "Assign the member to the right tribe, team, domain, and location."}
                                {activeStep === 2 &&
                                    "Add optional skills, certifications, and vendor information."}
                            </Typography>
                        </Box>

                        <Box className={classes.stepSection}>
                            {activeStep === 0 && (
                                <Box className={classes.formGrid}>
                                    <TextField
                                        label="Email"
                                        value={formData.email || ""}
                                        onChange={handleFormChange("email")}
                                        disabled={loading || !!editingMember}
                                        fullWidth
                                        type="email"
                                        size="small"
                                        variant="outlined"
                                        required
                                        error={isEmailInvalid}
                                        helperText={emailHelperText}
                                    />
                                    <TextField
                                        label="Name"
                                        value={formData.name || ""}
                                        onChange={handleFormChange("name")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        required
                                    />
                                </Box>
                            )}

                            {activeStep === 1 && (
                                <Box className={classes.formGrid}>
                                    <Autocomplete
                                        freeSolo
                                        autoSelect
                                        selectOnFocus
                                        clearOnBlur={false}
                                        options={visibleTribeOptions}
                                        value={formData.tribe || null}
                                        inputValue={formData.tribe || ""}
                                        onChange={handleAutocompleteChange("tribe")}
                                        onInputChange={handleAutocompleteInputChange("tribe")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        getOptionLabel={(option) => option}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Tribe"
                                                variant="outlined"
                                                required
                                                helperText="Select an existing tribe or type a custom tribe"
                                            />
                                        )}
                                    />
                                    <Autocomplete
                                        freeSolo
                                        autoSelect
                                        selectOnFocus
                                        clearOnBlur={false}
                                        options={visibleTeamOptions}
                                        value={formData.team || null}
                                        inputValue={formData.team || ""}
                                        onChange={handleAutocompleteChange("team")}
                                        onInputChange={handleAutocompleteInputChange("team")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        getOptionLabel={(option) => option}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Team"
                                                variant="outlined"
                                                required
                                                helperText="Select an existing team or type a custom team"
                                            />
                                        )}
                                    />
                                    <input
                                        type="hidden"
                                        value="cloud"
                                    />
                                    <Autocomplete
                                        freeSolo
                                        autoSelect
                                        selectOnFocus
                                        clearOnBlur={false}
                                        options={visibleLocationOptions}
                                        value={formData.location || null}
                                        inputValue={formData.location || ""}
                                        onChange={handleAutocompleteChange("location")}
                                        onInputChange={handleAutocompleteInputChange("location")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        getOptionLabel={(option) => option}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Location"
                                                variant="outlined"
                                                required
                                                helperText="Select an existing location or type a custom location"
                                            />
                                        )}
                                    />
                                </Box>
                            )}

                            {activeStep === 2 && (
                                <Box className={classes.formGrid}>
                                    <TextField
                                        label="Skills"
                                        value={formData.skills || ""}
                                        onChange={handleFormChange("skills")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        helperText="Comma-separated values"
                                        className={classes.fullWidthField}
                                    />
                                    <TextField
                                        label="Certifications"
                                        value={formData.certifications || ""}
                                        onChange={handleFormChange("certifications")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        multiline
                                        rows={2}
                                        helperText="Comma-separated values"
                                        className={classes.fullWidthField}
                                    />
                                    <TextField
                                        label="Vendor Name"
                                        value={formData.vendorname || ""}
                                        onChange={handleFormChange("vendorname")}
                                        disabled={loading}
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        className={classes.fullWidthField}
                                    />
                                </Box>
                            )}
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions className={classes.formDialogActions}>
                    <Button
                        onClick={handleCloseForm}
                        disabled={loading}
                        className={classes.secondaryButton}
                    >
                        Cancel
                    </Button>
                    <Box className={classes.actionGroup}>
                        <Button
                            onClick={handleBackStep}
                            disabled={loading || activeStep === 0}
                            className={classes.secondaryButton}
                        >
                            Back
                        </Button>
                        {isLastStep ? (
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                color="primary"
                                disabled={isSubmitDisabled}
                                className={classes.primaryButton}
                            >
                                {loading ? <CircularProgress size={22} /> : "Save changes"}
                            </Button>
                        ) : (
                            <Button
                                onClick={handleNextStep}
                                variant="contained"
                                color="primary"
                                disabled={isNextDisabled}
                                className={classes.primaryButton}
                            >
                                Next
                            </Button>
                        )}
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog
                open={Boolean(memberToDelete)}
                onClose={handleCloseDeleteDialog}
                maxWidth="sm"
                fullWidth
                aria-labelledby="delete-member-dialog-title"
                classes={{ paper: classes.deleteDialogPaper }}
            >
                <DialogTitle
                    disableTypography
                    id="delete-member-dialog-title"
                    className={classes.dialogTitle}
                >
                    <Box className={classes.header}>
                        <Box className={classes.headerLeft}>
                            <Box className={classes.deleteHeaderIcon}>
                                <DeleteIcon />
                            </Box>
                            <Box>
                                <Typography variant="h6" className={classes.titleText}>
                                    Delete Team Member
                                </Typography>
                                <Typography variant="body2" className={classes.subtitleText}>
                                    Confirm the member details before deleting this profile.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogTitle>

                <DialogContent className={classes.deleteContent}>
                    <Alert severity="warning" className={classes.alertBox}>
                        This action will remove the member from the team directory.
                    </Alert>

                    <Typography variant="body2" className={classes.sectionDescription}>
                        Please verify that this is the correct user before continuing.
                    </Typography>

                    <Box className={classes.deleteSummaryCard}>
                        <Box className={classes.deleteSummaryRow}>
                            <Typography className={classes.deleteSummaryLabel}>
                                Name
                            </Typography>
                            <Typography className={classes.deleteSummaryValue}>
                                {memberToDelete?.name?.trim() || "Not set"}
                            </Typography>
                        </Box>
                        <Box className={classes.deleteSummaryRow}>
                            <Typography className={classes.deleteSummaryLabel}>
                                Email
                            </Typography>
                            <Typography className={classes.deleteSummaryValue}>
                                {memberToDelete?.email?.trim() || "Not set"}
                            </Typography>
                        </Box>
                        <Box className={classes.deleteSummaryRow}>
                            <Typography className={classes.deleteSummaryLabel}>
                                Tribe
                            </Typography>
                            <Typography className={classes.deleteSummaryValue}>
                                {memberToDelete?.tribe?.trim() || "Not set"}
                            </Typography>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions className={classes.dialogActions}>
                    <Button
                        onClick={handleCloseDeleteDialog}
                        disabled={loading}
                        className={classes.secondaryButton}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmDelete}
                        disabled={loading || !memberToDelete?.email}
                        variant="contained"
                        className={classes.dangerButton}
                    >
                        {loading ? <CircularProgress size={22} /> : "Delete member"}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
